import MenuIcon from '@material-symbols/svg-300/rounded/menu.svg'
import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  userCanCommentOnRoot,
  userCanDeleteCommentOnRoot,
  userCanDeleteCommentOnRootPermanently,
  userCanEditCommentOnRoot,
} from '../../../state/calculations/permissions/userCan'
import useComment from '../../../state/queries/useComment'
import { useSession } from '../../../state/session'
import useRoutes from '../../../state/useRoutes'
import useDialog from '../../../utilities/useDialog'

import Button from '../../basics/Button/Button'
import ConfirmDialog from '../../basics/ConfirmDialog/ConfirmDialog'
import FormButtons from '../../basics/FormButtons/FormButtons'
import Menu from '../../basics/Menu/Menu'
import { TextAreaFormik } from '../../basics/TextArea/TextArea'
import UserAvatar from '../../UserAvatar/UserAvatar'
import useCreateComment from '../../../state/mutations/useCreateComment'
import useDeleteComment from '../../../state/mutations/useDeleteComment'
import { Comment, CommentRoot, CommentRootUuid } from '../../../models/Comment'
import EditCommentForm from '../EditCommentForm/EditCommentForm'

import styles from './CommentsList.module.scss'
import set from 'lodash/set'

/**********************************************************************
 * ReplyForm
 **********************************************************************/
type ReplyFormProps = {
  rootUuid: CommentRootUuid
  parentUuid: string
  level: number
  onClose: () => void
}

const ReplyForm = React.memo(function ReplyForm({ rootUuid, parentUuid, level, onClose }: ReplyFormProps) {
  const { t } = useTranslation()

  const createComment = useCreateComment()
  const formik = useFormik({
    initialValues: {
      ...rootUuid,
      parentUuid,
      text: '',
    },
    validate: (values) => {
      const errors = {}

      if (values.text && values.text.length > 2048) {
        set(errors, `text`, t('errors.fields.message.not longer then', { number: 2048 }))
      }

      return errors
    },
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await createComment.mutateAsync(values)
        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form
        noValidate
        onSubmit={formik.handleSubmit}
        className={styles.ReplyForm}
        style={{ '--depth': level - 1 } as any}
      >
        <TextAreaFormik name="text" label={t('comment.field text')} required />
        <FormButtons onClose={onClose} {...formik} />
      </form>
    </FormikProvider>
  )
})

/**********************************************************************
 * CommentRow
 **********************************************************************/
type CommentRowProps = {
  comment: Comment
  root: CommentRoot
  rootUuid: CommentRootUuid
  replyToCommentUuid?: string
  setReplyToCommentUuid: (uuid?: string) => void
}

const CommentRow = React.memo(function CommentRow({
  comment,
  root,
  rootUuid,
  replyToCommentUuid,
  setReplyToCommentUuid,
}: CommentRowProps) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()
  const showDialog = useDialog()
  const routes = useRoutes()

  const menuItems = []

  // reply
  if (comment.level < 3 && userCanCommentOnRoot(currentUser, root)) {
    menuItems.push(
      <li key="reply">
        <Button menuItem onClick={() => setReplyToCommentUuid(comment.uuid)}>
          {t('comment.cta reply')}
        </Button>
      </li>
    )
  }

  // edit
  const handleEdit = useCallback(async () => {
    await showDialog<void>((close) => (
      <EditCommentForm onClose={close} rootUuid={rootUuid} commentUuid={comment.uuid} />
    ))
  }, [rootUuid, comment])
  if (userCanEditCommentOnRoot(currentUser, comment, root)) {
    menuItems.push(
      <li key="edit">
        <Button menuItem onClick={handleEdit}>
          {t('generic cta.edit')}
        </Button>
      </li>
    )
  }

  // delete
  const deleteComment = useDeleteComment()
  const handleDelete = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => (
        <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
          <div>{t('comment.should comment be deleted')}</div>
        </ConfirmDialog>
      ),
      () => false
    )
    if (confirmed) {
      await deleteComment.mutateAsync({ commentUuid: comment.uuid, ...rootUuid })
    }
  }, [showDialog, comment])
  if (!comment.deletedAt && userCanDeleteCommentOnRoot(currentUser, comment, root)) {
    menuItems.push(
      <li key="delete">
        <Button menuItem onClick={handleDelete}>
          {t('comment.cta delete')}
        </Button>
      </li>
    )
  }

  // delete permanently
  const handleDeletePermanently = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => (
        <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
          <div>{t('comment.should comment be deleted permanently')}</div>
        </ConfirmDialog>
      ),
      () => false
    )
    if (confirmed) {
      await deleteComment.mutateAsync({ commentUuid: comment.uuid, ...rootUuid, permanent: true })
      if (!comment.parentUuid) {
        if ('groupUuid' in rootUuid) {
          routes.manageGroup.goto({ uuid: rootUuid.groupUuid })
        } else {
          routes.course.goto({ uuid: rootUuid.learningItemUuid })
        }
      }
    }
  }, [showDialog, comment])
  if (userCanDeleteCommentOnRootPermanently(currentUser, comment, root)) {
    menuItems.push(
      <li key="delete-permanently">
        <Button menuItem onClick={handleDeletePermanently}>
          {t('comment.cta delete permanently')}
        </Button>
      </li>
    )
  }

  return (
    <section
      className={classnames(styles.CommentRow, { skeleton: comment.isDummy })}
      style={{ '--depth': comment.level - 1 } as any}
    >
      <header>
        {comment.title && <h1>{comment.title}</h1>}
        <UserAvatar user={comment.user as any} className={styles.avatar} />
        <span className={styles.userName}>
          {comment.user.isAnonymous ? t('user.anonymous') : `${comment.user.firstname} ${comment.user.lastname}`}
        </span>
        <time dateTime={comment.createdAt.toISOString()}>{t('format.DATETIME', { datetime: comment.createdAt })}</time>
        <div className={styles.actions}>
          {menuItems.length > 0 && (
            <Menu small label={<MenuIcon />}>
              <ul>{menuItems}</ul>
            </Menu>
          )}
        </div>
      </header>
      <main>
        {comment.deletedAt ? (
          <span className={styles.deleted}>{t('comment.this comment was deleted')}</span>
        ) : (
          comment.text
        )}
      </main>
      {replyToCommentUuid === comment.uuid && (
        <section>
          <ReplyForm
            rootUuid={rootUuid}
            parentUuid={comment.uuid}
            level={comment.level + 1}
            onClose={() => setReplyToCommentUuid(undefined)}
          />
        </section>
      )}
      <section>
        {(comment.children || []).map((comment) => (
          <CommentRow
            key={comment.uuid}
            comment={comment}
            root={root}
            rootUuid={rootUuid}
            replyToCommentUuid={replyToCommentUuid}
            setReplyToCommentUuid={setReplyToCommentUuid}
          />
        ))}
      </section>
    </section>
  )
})

/**********************************************************************
 * CommentsList
 **********************************************************************/
type CommentsListProps = {
  commentUuid: string
  root: CommentRoot
  rootUuid: CommentRootUuid
}

export default React.memo(function CommentsList({ commentUuid, root, rootUuid }: CommentsListProps) {
  const [replyToCommentUuid, setReplyToCommentUuid] = useState<string | undefined>()

  const { comment } = useComment({ commentUuid, ...rootUuid })

  if (comment.error) {
    throw new Error('Comment not found')
  }

  return (
    <CommentRow
      comment={comment}
      root={root}
      rootUuid={rootUuid}
      replyToCommentUuid={replyToCommentUuid}
      setReplyToCommentUuid={setReplyToCommentUuid}
    />
  )
})
